import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { ArrowRight, Moon } from "lucide-react";

const SLUG = "pattern-break-package";
const TIME_SLOTS = ["09:00", "11:00", "14:00", "16:00", "18:00"];
const todayPlus = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().split("T")[0]; };

const SESSIONS = [
  { k: "Session 1 · The Reading", d: "A tarot map of your love pattern — we read the story before we rewrite it." },
  { k: "Sessions 2–4 · The Pattern Break", d: "Coaching through the wound behind the loop, one layer at a time." },
  { k: "Sessions 5–6 · The Rebuild", d: "Reclaim your identity. Love consciously — not from old pain." },
];

export default function Pattern() {
  usePageMeta(
    "The Pattern Break · 6-Session Package — Just be you by Mehjabin",
    "You did everything right — so why did it still end? A private 6-session arc to map, break, and rebuild your love pattern."
  );
  const [svc, setSvc] = useState(null);
  const { format, currency } = useCurrency();
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: todayPlus(2), time_slot: TIME_SLOTS[0], intention: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get(`/services/${SLUG}`).then((r) => setSvc(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", { service_slug: SLUG, ...form });
      const { data } = await api.post("/bookings/checkout", { booking_id: r.data.id, currency });
      openRazorpay(data, {
        onSuccess: () => {
          toast.success("Your package is reserved & paid. A confirmation is on its way to your inbox. ✨");
          setForm({ ...form, name: "", email: "", phone: "", intention: "" });
          setSubmitting(false);
        },
        onFailure: (dismissed) => {
          toast.error(dismissed
            ? "Your spot is reserved, but the payment wasn't completed. You can retry anytime — or WhatsApp me."
            : "Payment could not be completed. Please try again.");
          setSubmitting(false);
        },
      });
    } catch {
      toast.error("Something interrupted that. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="pattern-page" className="bg-[#16212f] text-[#faf8f5] min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 md:pt-24 pb-14">
        <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full border border-[#d4af37]/20" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#e8a888] mb-7" data-testid="pattern-kicker">
            6-Session Pattern Break Package · Online · 1:1
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.12]">
            You did everything right.<br />
            <span className="italic text-[#d4af37]">So why did it still end?</span>
          </h1>
          <Moon size={26} strokeWidth={1.3} className="mx-auto mt-10 text-[#d4af37]" />
          <div className="mt-10 flex flex-wrap justify-center items-center gap-3 text-sm font-serif italic" data-testid="pattern-loop">
            {["old wound", "hidden belief", "same choices", "same ending"].map((t, i) => (
              <span key={t} className="flex items-center gap-3">
                <span className="border border-[#d4af37]/50 rounded-full px-5 py-2 text-[#faf8f5]/85">{t}</span>
                {i < 3 && <span className="text-[#d4af37]">→</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SESSIONS */}
      <section className="max-w-2xl mx-auto px-6 space-y-5 pb-4">
        {SESSIONS.map((s, i) => (
          <motion.div
            key={s.k}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="border border-[#d4af37]/30 rounded-[1.5rem] bg-white/[0.03] px-7 py-6"
            data-testid={`pattern-session-${i + 1}`}
          >
            <div className="font-serif text-2xl text-[#d4af37]">{s.k}</div>
            <p className="mt-2 text-[#faf8f5]/75 text-sm md:text-base">{s.d}</p>
          </motion.div>
        ))}
        <div className="pt-8 text-center space-y-2.5 font-serif text-lg md:text-xl">
          <p>✦ Attract your soul-aligned partner</p>
          <p>✦ Rebuild your self-identity</p>
          <p>✦ Love consciously, not from old pain</p>
        </div>
      </section>

      {/* PRICE + FORM */}
      <section className="max-w-2xl mx-auto px-6 py-14 pb-24">
        <div className="text-center mb-10">
          <div className="font-serif text-5xl text-[#d4af37]" data-testid="pattern-price">{svc ? format(svc.price, svc.price_inr) : "₹15,000"}</div>
          <p className="mt-3 text-sm text-[#faf8f5]/60">six private sessions · online · yours across ~6 weeks</p>
        </div>
        <form onSubmit={submit} className="space-y-6 bg-white/[0.04] border border-[#d4af37]/25 rounded-[2rem] p-8 md:p-10" data-testid="pattern-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-[#e8a888] mb-2">Your name</label>
              <input required className="w-full bg-transparent border-b border-[#faf8f5]/30 focus:border-[#d4af37] outline-none py-2 text-[#faf8f5]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pattern-name-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-[#e8a888] mb-2">Email</label>
              <input required type="email" className="w-full bg-transparent border-b border-[#faf8f5]/30 focus:border-[#d4af37] outline-none py-2 text-[#faf8f5]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pattern-email-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-[#e8a888] mb-2">Phone (optional)</label>
              <input className="w-full bg-transparent border-b border-[#faf8f5]/30 focus:border-[#d4af37] outline-none py-2 text-[#faf8f5]" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pattern-phone-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-[#e8a888] mb-2">First session date</label>
              <input required type="date" min={todayPlus(1)} className="w-full bg-transparent border-b border-[#faf8f5]/30 focus:border-[#d4af37] outline-none py-2 text-[#faf8f5] [color-scheme:dark]" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pattern-date-input" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.24em] text-[#e8a888] mb-3">Preferred time</label>
            <div className="flex flex-wrap gap-2.5">
              {TIME_SLOTS.map((t) => (
                <button key={t} type="button" onClick={() => setForm({ ...form, time_slot: t })} data-testid={`pattern-slot-${t}`}
                  className={`px-4 py-2 rounded-full text-sm border transition ${form.time_slot === t ? "bg-[#d4af37] text-[#16212f] border-[#d4af37] font-semibold" : "border-[#faf8f5]/25 text-[#faf8f5]/75 hover:border-[#d4af37]"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.24em] text-[#e8a888] mb-2">What keeps ending the same way? (optional)</label>
            <textarea rows={3} className="w-full bg-transparent border-b border-[#faf8f5]/30 focus:border-[#d4af37] outline-none py-2 text-[#faf8f5] resize-none" value={form.intention} onChange={(e) => setForm({ ...form, intention: e.target.value })} data-testid="pattern-intention-input" />
          </div>
          <button type="submit" disabled={submitting} className="w-full rounded-full bg-[#d4af37] hover:bg-[#e0c158] text-[#16212f] font-bold tracking-[0.12em] uppercase text-sm py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pattern-reserve-btn">
            {submitting ? "Opening checkout…" : "Reserve your package"} <ArrowRight size={16} />
          </button>
          <p className="text-center text-xs text-[#faf8f5]/55">
            Prefer to pay in 2–3 parts?{" "}
            <a href="https://wa.me/919707788827" target="_blank" rel="noreferrer" className="text-[#d4af37] underline underline-offset-4" data-testid="pattern-whatsapp-link">WhatsApp me</a>
            {" "}— we'll arrange it, no pressure.
          </p>
        </form>
        <p className="mt-10 text-center font-serif italic text-[#e8a888]/80">with warmth, Mehjabin · justbeyou.co.in</p>
      </section>
    </div>
  );
}
